import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Ban } from "lucide-react";
import { adminService } from "../admin/services/adminService";
import { useAuth } from "../auth/hooks/useAuth";
import Modal from "../../components/ui/Modal";
import TrialPaymentStep from "../auth/components/TrialPaymentStep";
import {
  getSubscriptionStatus,
  cancelTrialSubscription,
} from "./services/dashboardService";

// ── Same mapping as AdminPricingManagementPage ────────────────────────────────
const apiToUi = (p) => {
  // analyses_per_month is the allowance the backend actually enforces (-1 = unlimited).
  // assets_limit is an older, unrelated number and must not stand in for it.
  const limit = p.analyses_per_month ?? p.assets_limit;
  return {
    id:               p.id,
    name:             p.name          ?? "",
    price:            Number(p.price) || 0,
    popular:          p.is_popular    ?? false,
    analysesPerMonth: limit,
    analysesLabel:    limit === -1 || limit == null ? "Unlimited" : String(limit),
    active:           p.is_active     ?? true,
    comingSoon:       p.is_active === false,
    features:         Array.isArray(p.features) ? p.features : [],
    currency:         p.currency      ?? "USD",
    stripePriceId:    p.stripe_price_id ?? "",
  };
};

const DotItem = ({ children }) => (
  <div className="flex items-center gap-2 text-sm text-brand-muted">
    <CheckCircle2 className="w-4 h-4 text-primary/80 flex-shrink-0" />
    <span>{children}</span>
  </div>
);

// ── Confirmation modal — works for both trialing and active plans ─────────────
const CancelModal = ({ isTrial, onCancel, onConfirm, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="w-full max-w-sm rounded-2xl overflow-hidden shadow-2xl">
      <div className="bg-gradient-to-b from-orange-500 to-orange-600 px-6 pt-6 pb-5 text-white">
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mb-4">
          <AlertTriangle className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-bold mb-1">
          {isTrial ? "Cancel Free Trial?" : "Cancel Plan?"}
        </h3>
        <p className="text-sm text-white/85 leading-relaxed">
          {isTrial ? (
            <>
              Are you sure you want to cancel your free trial?{" "}
              <span className="font-semibold text-white">You will not be charged.</span>{" "}
              Your trial will end immediately and trial features will be disabled.
            </>
          ) : (
            <>
              Are you sure you want to cancel your current plan?{" "}
              Your subscription will be canceled and access to paid features will end.
            </>
          )}
        </p>
        <div className="flex items-center gap-3 mt-5">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2.5 rounded-full border border-white text-white text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            {isTrial ? "Keep Trial" : "Keep Plan"}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2.5 rounded-full bg-white text-orange-600 text-sm font-semibold hover:bg-white/90 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Canceling…
              </>
            ) : isTrial ? "Yes, Cancel Trial" : "Yes, Cancel Plan"}
          </button>
        </div>
      </div>
      <div className="bg-white px-6 py-3 text-center">
        <p className="text-xs text-brand-muted">
          {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </p>
      </div>
    </div>
  </div>
);

// ── Floating toast notification ───────────────────────────────────────────────
const Toast = ({ type, message, onDismiss }) => {
  useEffect(() => {
    const id = setTimeout(onDismiss, 4500);
    return () => clearTimeout(id);
  }, [onDismiss]);

  const isSuccess = type === "success";
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
      <div
        className={`pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold text-white ${
          isSuccess ? "bg-emerald-600" : "bg-danger"
        }`}
      >
        {isSuccess ? (
          <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        ) : (
          <XCircle className="w-4 h-4 flex-shrink-0" />
        )}
        {message}
      </div>
    </div>
  );
};

const UpgradeErrorModal = ({ message, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
      <div className="flex flex-col items-center text-center gap-3">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
          <XCircle className="w-6 h-6 text-danger" />
        </div>
        <h3 className="text-base font-bold text-brand-text">Unable to Upgrade</h3>
        <p className="text-sm text-brand-muted leading-relaxed">{message}</p>
        <button
          onClick={onClose}
          className="mt-2 w-full h-10 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary-dark transition-colors"
        >
          OK
        </button>
      </div>
    </div>
  </div>
);

const SubscriptionPage = () => {
  const navigate = useNavigate();

  const { state: authState } = useAuth();
  const [plans, setPlans]               = useState([]);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [upgrading, setUpgrading]       = useState(null); // plan.id currently being initiated
  const [upgradeError, setUpgradeError] = useState(null);

  // ── Subscription / trial status — sourced from GET /api/v1/subscriptions/status ──
  const [planStatus, setPlanStatus]           = useState(null); // always from API, never stale auth cache
  const [statusLoaded, setStatusLoaded]       = useState(false); // true once first API response received
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading]     = useState(false);
  const [toast, setToast]                     = useState(null); // { type: 'success'|'error', message }

  const dismissToast = useCallback(() => setToast(null), []);

  // Shared helper — always reads data.subscription.status from the status API
  const refreshStatus = useCallback(() =>
    getSubscriptionStatus()
      .then((res) => {
        // API response shape: { success, data: { subscription: { status }, current_plan: { id } } }
        const data   = res?.data ?? {};
        const status = data?.subscription?.status ?? null;
        const cpId   = data?.current_plan?.id ?? null;

        if (status) setPlanStatus(status);

        if (status === "canceled") {
          setCurrentPlanId(null);
        } else if (cpId) {
          setCurrentPlanId(cpId);
        }
      })
      .catch(() => {})
      .finally(() => setStatusLoaded(true)),
  []);

  // refreshStatus is called after plans load (see getPlans effect below)

  // ── Cancel handler ────────────────────────────────────────────────────────
  const handleCancelTrialConfirm = async () => {
    setCancelLoading(true);
    const wasTrialing = planStatus === "trialing";
    try {
      await cancelTrialSubscription();
      await refreshStatus(); // re-reads data.subscription.status
      setShowCancelModal(false);
      setToast({
        type: "success",
        message: wasTrialing ? "Your free trial has been canceled." : "Your plan has been canceled.",
      });
    } catch (err) {
      setShowCancelModal(false);
      const msg =
        err?.response?.data?.message ||
        (err?.message?.toLowerCase().includes("network")
          ? "Something went wrong. Please check your connection."
          : "Unable to cancel. Please try again.");
      setToast({ type: "error", message: msg });
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSubscriptionSuccess = (subscriptionData) => {
    setSelectedPlan(null);
    refreshStatus(); // re-fetch so planStatus updates and canceled banner disappears
    navigate('/dashboard', {
      state: { message: 'Subscription activated successfully!', subscriptionData },
    });
  };

  // ── New subscription — same flow as the trial card prompt ──
  // Card first, then POST /api/v1/subscriptions, where the charge completes
  // server-side and the payment record and confirmation emails follow.
  // (The previous flow pre-created an unpaid Stripe subscription and confirmed
  // in the browser, so the server never saw a completed payment: no emails, and
  // the transaction row stayed "pending".)
  const handleNewSubscription = (plan) => {
    setUpgradeError(null);
    setSelectedPlan(plan);
  };

  useEffect(() => {
    let cancelled = false;
    adminService.getPlans()
      .then((res) => {
        if (cancelled) return;
        const raw = Array.isArray(res)              ? res
                  : Array.isArray(res?.data?.plans) ? res.data.plans
                  : Array.isArray(res?.data)        ? res.data
                  : [];
        setPlans(
          raw
            // Coming Soon plans stay listed here too - disabled, not hidden.
            .map(apiToUi)
        );

        // refreshStatus() called below is the authoritative source for currentPlanId
        const cp = res?.data?.currentPlan;
        if (cp?.id) setCurrentPlanId(cp.id);

        setError(null);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          // Always let refreshStatus have the final say so cancel state is reflected
          refreshStatus();
        }
      });
    return () => { cancelled = true; };
  }, [refreshStatus]);

  return (
    <div className="flex flex-col gap-4">
      {/* ── Modals ── */}
      <Modal
        isOpen={!!selectedPlan}
        onClose={() => setSelectedPlan(null)}
        title={selectedPlan ? `Subscribe to ${selectedPlan.name}` : ""}
      >
        {selectedPlan && (
          <TrialPaymentStep
            planId={selectedPlan.id}
            userName={authState?.user?.name}
            userEmail={authState?.user?.email}
            planName={selectedPlan.name}
            planPrice={selectedPlan.price}
            mode="upgrade"
            onSuccess={() => handleSubscriptionSuccess({ plan: selectedPlan.name })}
            onBack={() => setSelectedPlan(null)}
          />
        )}
      </Modal>

      {showCancelModal && (
        <CancelModal
          isTrial={planStatus === "trialing"}
          loading={cancelLoading}
          onCancel={() => setShowCancelModal(false)}
          onConfirm={handleCancelTrialConfirm}
        />
      )}
 
      {/* ── Toast ── */}
       {toast && (
        <Toast type={toast.type} message={toast.message} onDismiss={dismissToast} />
      )} 

      {upgradeError && (
        <UpgradeErrorModal message={upgradeError} onClose={() => setUpgradeError(null)} />
      )}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-text">Subscription</h1>
          <p className="text-sm text-brand-muted mt-0.5">Manage your plan, usage, and billing.</p>
        </div>

        {/* Cancel button — only render after status confirmed from API */}
        {statusLoaded && (planStatus === "trialing" || planStatus === "active") && (
          <button
            onClick={() => setShowCancelModal(true)}
            className="inline-flex items-center gap-2 border border-danger/30 bg-danger/5 text-danger px-4 py-2 rounded-xl text-sm font-semibold hover:bg-danger/10 transition-colors"
          >
            <Ban className="w-4 h-4" />
            {planStatus === "trialing" ? "Cancel Free Trial" : "Cancel Plan"}
          </button>
        )}
      </div>

      {/* ── Canceled notice — only shown after API confirms canceled status ── */}
      {statusLoaded && planStatus === "canceled" && (
        <div className="flex items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 px-5 py-4">
          <XCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-orange-600">Your subscription has been canceled</p>
            <p className="text-xs text-orange-400 mt-0.5">
              Upgrade to a paid plan to continue accessing premium features.
            </p>
          </div>
        </div>
      )}

      {/* ── Plans from API ── */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-brand-muted gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Loading plans…</span>
        </div>
      )}

      {!loading && error && (
        <p className="text-center text-sm text-danger py-6">{error}</p>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isCurrent = plan.id === currentPlanId;
            const isHighlighted = isCurrent;
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl flex flex-col transition-shadow ${
                  isHighlighted
                    ? "border-2 border-primary shadow-lg shadow-primary/10"
                    : "border border-surface-border shadow-sm"
                }`}
              >
                {/* Coming Soon pill — "Popular" is retired */}
                {plan.comingSoon && (
                  <div className="px-5 pt-4 pb-0">
                    <span className="inline-flex items-center bg-slate-200 text-slate-700 text-xs font-semibold px-3 py-1 rounded-full">
                      Coming Soon
                    </span>
                  </div>
                )}

                <div className={`px-5 flex flex-col flex-1 ${plan.comingSoon ? "pt-3 pb-5" : "pt-5 pb-5"}`}>
                  <p className="text-base font-bold text-brand-text">{plan.name}</p>

                  <div className="flex items-end gap-1 mt-1.5">
                    <p className="text-4xl font-extrabold text-brand-text leading-none">
                      ${plan.price}
                    </p>
                    <span className="text-sm text-brand-light mb-1">/mo</span>
                  </div>

                  <p className="text-xs text-brand-muted mt-1.5">
                    {plan.analysesLabel} analyses/month
                  </p>

                  {/* Divider */}
                  <div className="border-t border-surface-border my-3" />

                  <div className="space-y-2 flex-1">
                    {plan.features.map((f) => (
                      <DotItem key={f}>{f}</DotItem>
                    ))}
                  </div>

                  <button
                    onClick={() => !isCurrent && !plan.comingSoon && handleNewSubscription(plan)}
                    className={`w-full h-10 mt-5 rounded-xl text-sm font-semibold transition-colors ${
                      plan.comingSoon
                        ? "border border-surface-border text-brand-muted bg-slate-50 cursor-not-allowed"
                        : isCurrent
                        ? "border border-primary/30 text-primary bg-primary/5 cursor-default"
                        : "bg-primary text-white hover:bg-primary-dark"
                    }`}
                    disabled={isCurrent || plan.comingSoon}
                  >
                    {upgrading === plan.id ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" /> Preparing…
                      </span>
                    ) : plan.comingSoon ? "Coming Soon" : isCurrent ? "Current Plan" : "Upgrade"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
